// [S-388] Draft actions kebab — Open / Rename / Delete, shared by the Drafts
// index cards and the DraftDetail header. Delete + Rename are owner-only
// (the service enforces this too; the UI hides the affordances for clarity).
// Destructive delete routes through a confirmation Dialog.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogClose,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  LoadingState,
  useToast,
} from "../index";
import { getApiErrorMessage } from "../../data/api";
import {
  useDeleteDraft,
  useExportDraft,
  usePromoteDraftToLongForm,
  useUpdateDraft,
  type Draft,
} from "../../data/draftsQuery";

export function DraftActionsMenu({
  draft,
  isOwner,
  align = "end",
  onDeleted,
}: {
  draft: Draft;
  isOwner: boolean;
  align?: "start" | "center" | "end";
  /** Called after a successful delete (e.g. navigate away from the detail). */
  onDeleted?: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const exportDraft = useExportDraft();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [promoting, setPromoting] = useState(false);

  // [S-635] Download the draft as a Word .docx. Surfaces the 422 "empty draft"
  // (and any other) error as a toast rather than failing silently.
  const handleExport = (): void => {
    exportDraft.mutate(draft.id, {
      onError: (e) =>
        toast.push({
          variant: "error",
          title: "Couldn't export this draft",
          description: getApiErrorMessage(e) ?? e.message,
        }),
    });
  };

  return (
    <>
      <DropdownMenu
        align={align}
        trigger={
          <button
            type="button"
            className="dft__kebab"
            aria-label="Draft actions"
            onClick={(e) => e.preventDefault()}
          >
            <KebabIcon />
          </button>
        }
      >
        <DropdownMenuItem
          onSelect={() => navigate(`/drafts/${encodeURIComponent(draft.id)}`)}
        >
          Open
        </DropdownMenuItem>
        {/* [S-633] Generate a full long-form contract from this closed deal
            memo, reusing the certified long-form template + the memo's terms. */}
        <DropdownMenuItem onSelect={() => setPromoting(true)}>
          Generate long form
        </DropdownMenuItem>
        {/* [S-635] Download the current draft as a Word document. */}
        <DropdownMenuItem
          onSelect={handleExport}
          data-testid={`draft-export-${draft.id}`}
        >
          {exportDraft.isPending ? "Preparing DOCX…" : "Download as DOCX"}
        </DropdownMenuItem>
        {isOwner && (
          <DropdownMenuItem onSelect={() => setRenaming(true)}>
            Rename
          </DropdownMenuItem>
        )}
        {isOwner && <DropdownMenuSeparator className="ds-menu__sep" />}
        {isOwner && (
          <DropdownMenuItem
            className="ds-menu__item ds-menu__item--danger"
            onSelect={() => setConfirmDelete(true)}
          >
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenu>

      {confirmDelete && (
        <DeleteDraftDialog
          draft={draft}
          onClose={() => setConfirmDelete(false)}
          onDeleted={onDeleted}
        />
      )}
      {renaming && (
        <RenameDraftDialog draft={draft} onClose={() => setRenaming(false)} />
      )}
      {promoting && (
        <PromoteToLongFormDialog
          draft={draft}
          onClose={() => setPromoting(false)}
        />
      )}
    </>
  );
}

// [S-633] Generate a long-form contract from a closed deal memo. Confirms, calls
// the promotion endpoint, and on success opens the newly-created long-form draft.
// Honest error surface — a 422 (no certified long-form template / empty memo),
// 501 (not configured), or 502 (generation failed) shows the reason, never a
// fake success.
function PromoteToLongFormDialog({
  draft,
  onClose,
}: {
  draft: Draft;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const promote = usePromoteDraftToLongForm();
  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o && !promote.isPending) onClose();
      }}
      title="Generate long form"
      description={`Generate a full long-form contract from "${draft.title || "Untitled draft"}", using the certified long-form template for this deal type and the memo's terms. It opens as a new draft you can edit.`}
    >
      {promote.isError && (
        <div className="prec__state" role="alert" data-testid="promote-error">
          {getApiErrorMessage(promote.error) ?? promote.error.message}
        </div>
      )}
      {promote.isPending && (
        <div className="dft__promote__progress" data-testid="promote-progress">
          <LoadingState label="Generating the long-form contract from your deal memo — this can take up to a minute. Keep this window open." />
        </div>
      )}
      <div className="dft__form__actions">
        <Button variant="ghost" onClick={onClose} disabled={promote.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={promote.isPending}
          data-testid="promote-confirm"
          onClick={() =>
            promote.mutate(draft.id, {
              onSuccess: (res) => {
                onClose();
                navigate(`/drafts/${encodeURIComponent(res.draftId)}`);
              },
            })
          }
        >
          {promote.isPending ? "Generating…" : "Generate long form"}
        </Button>
      </div>
    </Dialog>
  );
}

function DeleteDraftDialog({
  draft,
  onClose,
  onDeleted,
}: {
  draft: Draft;
  onClose: () => void;
  onDeleted?: () => void;
}): JSX.Element {
  const del = useDeleteDraft();
  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o && !del.isPending) onClose();
      }}
      title="Delete draft"
      description={`Permanently delete "${draft.title || "Untitled draft"}", including all versions and reviews? This can't be undone.`}
    >
      {del.isError && (
        <div className="prec__state" role="alert">
          {getApiErrorMessage(del.error) ?? del.error.message}
        </div>
      )}
      <div className="dft__form__actions">
        <Button variant="ghost" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button
          variant="override"
          disabled={del.isPending}
          onClick={() =>
            del.mutate(draft.id, {
              onSuccess: () => {
                onClose();
                onDeleted?.();
              },
            })
          }
        >
          {del.isPending ? "Deleting…" : "Delete draft"}
        </Button>
      </div>
    </Dialog>
  );
}

function RenameDraftDialog({
  draft,
  onClose,
}: {
  draft: Draft;
  onClose: () => void;
}): JSX.Element {
  const update = useUpdateDraft();
  const [title, setTitle] = useState(draft.title);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (title.trim().length === 0 || update.isPending) return;
    update.mutate(
      { id: draft.id, patch: { title: title.trim() } },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o && !update.isPending) onClose();
      }}
      title="Rename draft"
    >
      <form onSubmit={handleSubmit} className="dft__form">
        <label className="dft__field">
          <span className="dft__field__lbl">Title</span>
          <input
            className="dft__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </label>
        {update.isError && (
          <div className="prec__state" role="alert">
            {getApiErrorMessage(update.error) ?? update.error.message}
          </div>
        )}
        <div className="dft__form__actions">
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            variant="primary"
            disabled={title.trim().length === 0 || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function KebabIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}
